import type { SessionConversation } from '@heidi/evidence/contracts';
import { generateId } from '@heidi/utils';

import { EvidenceSBSStreamStatus } from './types';

/**
 * Per-candidate SBS stream state. Lives in Jotai (see `atoms.ts`) so an
 * in-flight stream survives the Evidence panel unmounting on a tab switch —
 * the same persistence model normal Evidence Q&A uses via `chatStateAtom`.
 * Kept in its own module so both the atoms and `useEvidenceSBSStreams` can
 * import it without an atoms ↔ hook import cycle.
 */
export interface SBSStreamState {
  conversation: SessionConversation;
  isStreaming: boolean;
  status: EvidenceSBSStreamStatus;
  error: string | null;
}

export function makeInitialConversation(): SessionConversation {
  return {
    timeline: [],
    streamingState: null,
    metadata: { lastActivity: Date.now() },
  };
}

export function makeStreamingConversation(): SessionConversation {
  const now = Date.now();
  return {
    timeline: [
      {
        type: 'reasoning_item' as const,
        id: generateId(),
        content: [],
        position: 0,
        backendIndex: -1,
      },
    ],
    streamingState: {
      basePosition: 0,
      activeIndices: new Map(),
      currentActiveIndex: null,
      activityGroupOpen: true,
      streamStartedAt: now,
      lastChunkAt: now,
      lastVisibleContentAt: now,
    },
    metadata: { lastActivity: now },
  };
}

export function makeInitialStreamState(): SBSStreamState {
  return {
    conversation: makeInitialConversation(),
    isStreaming: false,
    status: EvidenceSBSStreamStatus.WAITING_START,
    error: null,
  };
}
